/**
 * Object identity — the ONE map of a business object kind (and, by extension, a
 * section/mode) to its {icon, color}. Created 2026-07-21 from the app-wide
 * iconography audit, which found the truth split across four partial homes that
 * disagreed: a task wore FIVE different glyphs (CircleDot / ClipboardList /
 * FolderKanban / Boxes / circle-check) depending on the surface, `person`,
 * `lead` and `candidate` drifted across the UserRound / User / Users family, and
 * kind COLOR did not exist as a concept at all.
 *
 * The rules this module enforces:
 *   1. ONE icon per kind, everywhere. A kind's masthead glyph, its create-menu
 *      entry, its dock group and its list badge all read this map.
 *   2. Color is ALWAYS section-tied — there is one color source (`sectionColor`
 *      in section-colors.ts), never a second hardcoded hex. `person`, `lead`
 *      and `candidate` deliberately SHARE the `UserRound` glyph and are told
 *      apart by their section color (a lead is sales-blue, a directory person is
 *      customer-success-teal, a candidate is hiring-emerald) — icon says WHAT it
 *      is, color says WHERE it lives.
 *
 * NOT a home for status/priority glyphs (those are correctly state-driven —
 * status-ring.tsx, priority-icon.tsx) or the Pulse TouchKind icons (a different
 * vocabulary). This is kind/section IDENTITY only.
 *
 * A lockstep guard (`pnpm test-object-identity`, in BOTH verify:checks chains)
 * asserts every kind in `RESOLVABLE_KINDS` has an entry here, that every entry's
 * `section` resolves to a real `SECTION_COLORS` slug (or the agents sentinel),
 * and that no kind token reaches the UI raw.
 *
 * That guard is new as of 2026-07-22. This header described it for a year before
 * it existed, and in the meantime the map had already lapsed: `action`,
 * `scorecard`, `competitor`, `swot`, `icp`, `document` and `sequence` all resolve
 * to real pages and had no entry, so every surface asking for their identity got
 * the fallback box and a neutral color. Adding a kind to `RESOLVABLE_KINDS` now
 * fails the gate until it is given an identity here.
 */

import type * as React from "react";
import {
  Box,
  Briefcase,
  Building2,
  CircleDot,
  ClipboardCheck,
  Crosshair,
  FileText,
  Handshake,
  LayoutGrid,
  Send,
  Swords,
  Target,
  UserRound,
  Workflow,
  Zap,
} from "../icons";
import { sectionColor } from "./section-colors";
import { titleCase } from "../format";

type Glyph = React.ComponentType<{ className?: string }>;

/**
 * Kind → {icon, section}. `section` is the slug whose color the kind takes; it
 * is a real section-colors slug for everything that lives in a business section,
 * and the sentinel `"agents"` for the fleet kinds (goal, workflow) that have no
 * canonical section color and take the agentic accent instead.
 */
export const OBJECT_KIND_IDENTITY: Readonly<
  Record<string, { icon: Glyph; section: string }>
> = {
  // Product / delivery
  task: { icon: CircleDot, section: "development" },
  project: { icon: Box, section: "development" },
  // A generated artifact. Shares `FileText` with `application` on the same terms
  // as the UserRound family below: icon says WHAT it is (a document), section
  // color says WHERE it lives (development amber vs hiring emerald).
  document: { icon: FileText, section: "development" },
  // Agents (fleet) — no canonical section color; takes the agentic accent
  goal: { icon: Target, section: "agents" },
  workflow: { icon: Workflow, section: "agents" },
  action: { icon: Zap, section: "agents" },
  // Customers
  lead: { icon: UserRound, section: "sales" },
  deal: { icon: Handshake, section: "sales" },
  opportunity: { icon: Handshake, section: "sales" },
  sequence: { icon: Send, section: "sales" },
  organization: { icon: Building2, section: "operations" },
  customer: { icon: Building2, section: "operations" },
  person: { icon: UserRound, section: "customer-success" },
  // Market intelligence
  competitor: { icon: Swords, section: "marketing" },
  swot: { icon: LayoutGrid, section: "marketing" },
  icp: { icon: Crosshair, section: "marketing" },
  // Operations / hiring
  candidate: { icon: UserRound, section: "hiring" },
  job: { icon: Briefcase, section: "hiring" },
  application: { icon: FileText, section: "hiring" },
  scorecard: { icon: ClipboardCheck, section: "hiring" },
};

/** The agentic accent (goal/workflow) — the one color not in section-colors. */
const AGENTS_ACCENT = "#06B6D4";

/** The fallback glyph for a kind with no entry (a new/unknown kind). */
const FALLBACK_ICON: Glyph = Box;

/** The single icon for a kind — the same glyph on every surface. */
export function iconForKind(kind: string): Glyph {
  return OBJECT_KIND_IDENTITY[kind]?.icon ?? FALLBACK_ICON;
}

/**
 * A kind's DISPLAY NOUN — "task" → "Task", "key_result" → "Key Result".
 *
 * Lives here because it is the third face of the same identity the icon and the
 * color already come from: a masthead that draws the glyph and then spells the
 * noun must not get the noun from a second, drifting place (which is exactly the
 * split this module was created to end).
 *
 * Derived, not enumerated — `titleCase` handles every kind token in the map and
 * every kind that joins it later, so a new `loadObject` kind cannot show up
 * unlabeled. OVERRIDES carry only the words title-casing gets WRONG, which today
 * is the acronym class; add to it exactly when a token reads wrong, never as a
 * routine second entry per kind.
 */
const KIND_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  kpi: "KPI",
  okr: "OKR",
};

export function labelForKind(kind: string): string {
  const token = kind.trim();
  if (!token) return "";
  return KIND_LABEL_OVERRIDES[token.toLowerCase()] ?? titleCase(token);
}

/**
 * The kind's color — its section's accent, from the ONE color source
 * (`sectionColor`). Fleet kinds take the agentic cyan; an unknown kind takes the
 * neutral `sectionColor(null)`.
 */
export function colorForKind(kind: string): string {
  const id = OBJECT_KIND_IDENTITY[kind];
  if (!id) return sectionColor(null);
  return id.section === "agents" ? AGENTS_ACCENT : sectionColor(id.section);
}
